import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiError, type CasoListItem } from "@/lib/api";
import { useToast } from "@/ui/Toast";
import { SkeletonRows } from "@/ui/Skeleton";
import ActividadPill from "@/ui/ActividadPill";
import AyudaVista from "@/ui/AyudaVista";
import ImeisLista from "@/components/casos/ImeisLista";
import { ayudaProveedorPendiente } from "@/components/ayuda/vistas/proveedorPendiente";
import { ESTADO_CASO_PILL } from "@/lib/estadoCaso";
import { guardarRecorrido } from "@/lib/recorridoCasos";
import {
  CriticoBadge,
  EsperanBadge,
  PosesionBadge,
  TicketProveedorBadge,
  diasDesde,
} from "@/components/casos/BadgesCaso";

/** Chips de IMEI visibles por caso, igual que en la lista de casos. */
const MAX_IMEIS = 3;

function tonoDias(d: number): string {
  if (d >= 5) return "border-red-500/40 bg-red-500/10 text-red-300";
  if (d >= 2) return "border-amber-500/40 bg-amber-500/10 text-amber-300";
  return "border-ink-600 bg-ink-800 text-zinc-300";
}

/** Sin el año: la tabla ya trae 8 columnas y «2026» no distingue nada aquí
 *  (todo lo que sale en este tablero es de los últimos días). */
function fechaLocal(iso: string): string {
  return new Date(iso).toLocaleString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** De qué proveedor es el ticket del caso (para la ficha y el filtro). */
function proveedorDe(c: CasoListItem): string {
  if (c.jimi_work_order_no) return "Jimi";
  if (c.queclink_ticket_id) return "Queclink";
  return "—";
}

function ticketDe(c: CasoListItem): string | null {
  if (c.jimi_work_order_no) return c.jimi_work_order_no;
  if (c.queclink_ticket_id) return String(c.queclink_ticket_id);
  return null;
}

/**
 * Tablero de tickets con respuesta del proveedor esperando la NUESTRA.
 *
 * Existe porque la app ya traía las respuestas del FAE a la bitácora y avisaba
 * al asignado, pero el aviso se entierra y la respuesta se queda ahí: cuando se
 * midió (2026-07-29) había 13 casos abiertos esperando, ocho de ellos con seis
 * días. O sea, el mismo problema que cuando no se revisaba el portal, solo
 * movido de lugar.
 *
 * ⚠️ Se alimenta del MISMO endpoint que la lista de casos
 * (`GET /api/casos?proveedor_pendiente=1&abiertos=1`), no de uno propio. Lo pidió
 * Joe con el argumento correcto: «al final del día siguen siendo casos». Así el
 * tablero hereda gratis el buscador (cliente, contacto, folio, TE, ticket de
 * Jimi, marca, modelo, descripción e IMEI, sin acentos) y las mismas fichas que
 * la lista, en vez de mantener dos verdades del mismo caso. El endpoint
 * `/casos/proveedor-pendiente` sigue vivo para los contadores del Panel y de la
 * barra, que solo piden el total.
 *
 * Los días se cuentan desde que escribió el PROVEEDOR
 * (`casos.proveedor_respondio_at`), no desde que nuestro worker lo importó: al
 * traer un rezago todo llega "hoy" y un ticket contestado hace una semana se
 * vería recién llegado.
 */
export default function ProveedorPendiente() {
  const toast = useToast();
  const nav = useNavigate();

  const [casos, setCasos] = useState<CasoListItem[]>([]);
  const [cargando, setCargando] = useState(true);

  // El buscador va al servidor (es el mismo `q` de /casos, que sabe buscar por
  // IMEI y sin acentos). Los demás filtros son locales: la lista tope es de 200
  // y traerla de nuevo por cambiar un select solo agrega espera.
  const [recargar, setRecargar] = useState(0);
  // El toast se usa dentro del efecto sin ser dependencia suya (ver abajo).
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [fProveedor, setFProveedor] = useState("");
  const [fEstado, setFEstado] = useState("");
  const [fAsignado, setFAsignado] = useState("");

  useEffect(() => {
    const id = window.setTimeout(() => setQDebounced(q.trim()), 300);
    return () => window.clearTimeout(id);
  }, [q]);

  // ⚠️ El efecto NO puede depender de `toast`: `useToast()` devuelve un objeto
  // nuevo en cada render, así que un `useCallback([toast])` se recrea siempre y
  // el efecto se vuelve a disparar solo — bucle infinito de peticiones (medido:
  // ~100 llamadas en 4 s contra producción). Las dependencias reales son el
  // texto buscado y el botón «Actualizar».
  useEffect(() => {
    let vivo = true;
    void (async () => {
      setCargando(true);
      try {
        const params = new URLSearchParams({
          proveedor_pendiente: "1",
          abiertos: "1",
          page_size: "200",
        });
        if (qDebounced) params.set("q", qDebounced);
        const r = await api.get<{ items: CasoListItem[]; total: number }>(
          `/api/casos?${params.toString()}`,
        );
        // El más viejo primero: ese es el que urge. Se ordena aquí porque el
        // orden del endpoint de casos no contempla «cuándo contestó el proveedor».
        const items = [...r.items].sort((a, b) =>
          (a.proveedor_respondio_at ?? "").localeCompare(b.proveedor_respondio_at ?? ""),
        );
        // `vivo` descarta la respuesta de una búsqueda que ya quedó atrás: al
        // teclear rápido pueden volver en desorden y pintar el resultado viejo.
        if (vivo) setCasos(items);
      } catch (err) {
        const msg = err instanceof ApiError ? err.detail : "Error";
        if (vivo) toastRef.current.error("No pude cargar los pendientes", msg);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [qDebounced, recargar]);

  // Las opciones salen de lo que HAY (sobre el resultado del buscador, antes de
  // los filtros locales): así ningún select ofrece una opción que daría cero, ni
  // se encoge al elegir otro filtro.
  const opciones = useMemo(() => {
    const proveedores = new Set<string>();
    const estados = new Set<string>();
    const asignados = new Map<number, string>();
    for (const c of casos) {
      proveedores.add(proveedorDe(c));
      estados.add(c.estado);
      if (c.asignado_a_id) asignados.set(c.asignado_a_id, c.asignado_a_nombre ?? "—");
    }
    return {
      proveedores: [...proveedores].sort(),
      estados: [...estados].sort(),
      asignados: [...asignados.entries()].sort((a, b) => a[1].localeCompare(b[1])),
    };
  }, [casos]);

  const items = useMemo(
    () =>
      casos.filter((c) => {
        if (fProveedor && proveedorDe(c) !== fProveedor) return false;
        if (fEstado && c.estado !== fEstado) return false;
        if (fAsignado === "__sin__" && c.asignado_a_id) return false;
        if (fAsignado && fAsignado !== "__sin__" && String(c.asignado_a_id) !== fAsignado)
          return false;
        return true;
      }),
    [casos, fProveedor, fEstado, fAsignado],
  );

  const filtrando = Boolean(qDebounced || fProveedor || fEstado || fAsignado);
  const masViejo = useMemo(
    () =>
      items.reduce(
        (max, c) => Math.max(max, c.proveedor_respondio_at ? diasDesde(c.proveedor_respondio_at) : 0),
        0,
      ),
    [items],
  );

  /**
   * Al entrar a un caso desde aquí, el recorrido de «‹ Anterior · N de M ·
   * Siguiente ›» (y el gesto del dedo) es ESTA lista —con los filtros puestos—,
   * no la de /casos: al final son casos, y se recorren igual. Así se pueden
   * despachar los pendientes uno tras otro sin volver al tablero.
   */
  function sembrarRecorrido() {
    guardarRecorrido(items.map((i) => i.folio));
  }

  function abrir(folio: string) {
    sembrarRecorrido();
    nav(`/casos/${folio}`);
  }

  function limpiar() {
    setQ("");
    setFProveedor("");
    setFEstado("");
    setFAsignado("");
  }

  const selectCls =
    "w-full sm:w-auto bg-ink-800 border border-ink-600 rounded px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-accent-500";

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold">El proveedor contestó</h1>
            <AyudaVista titulo="El proveedor contestó" {...ayudaProveedorPendiente()} />
          </div>
          <p className="text-xs text-zinc-500">
            Tickets donde Jimi o Queclink ya respondieron y <strong>nosotros</strong> todavía
            no. Los más viejos primero.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setRecargar((n) => n + 1)}
            className="btn-ghost text-xs border border-ink-600"
          >
            ⟳ Actualizar
          </button>
        </div>
      </div>

      {/* Buscador + filtros. No se reusa `CasosFiltros`: ese trae vista
          activos/histórico, agrupado, asesores y KPIs, que aquí no aplican. */}
      <div className="card space-y-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por cliente, folio, TE, ticket, IMEI, marca o modelo…"
          className="input text-sm"
        />
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <select
            value={fProveedor}
            onChange={(e) => setFProveedor(e.target.value)}
            className={selectCls}
            aria-label="Filtrar por proveedor"
          >
            <option value="">Todos los proveedores</option>
            {opciones.proveedores.map((p) => (
              <option key={p} value={p}>
                {p === "—" ? "Sin ticket de proveedor" : p}
              </option>
            ))}
          </select>
          <select
            value={fEstado}
            onChange={(e) => setFEstado(e.target.value)}
            className={selectCls}
            aria-label="Filtrar por estado"
          >
            <option value="">Todos los estados</option>
            {opciones.estados.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
          <select
            value={fAsignado}
            onChange={(e) => setFAsignado(e.target.value)}
            className={selectCls}
            aria-label="Filtrar por responsable"
          >
            <option value="">Todos los responsables</option>
            <option value="__sin__">— sin asignar —</option>
            {opciones.asignados.map(([id, nombre]) => (
              <option key={id} value={String(id)}>
                {nombre}
              </option>
            ))}
          </select>
          {filtrando && (
            <button
              type="button"
              onClick={limpiar}
              className="text-xs text-zinc-400 hover:text-accent-300 underline w-full sm:w-auto text-left"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {cargando ? (
        <div className="card">
          <SkeletonRows rows={6} />
        </div>
      ) : (
        <>
          <div className="card flex flex-wrap items-center gap-3 text-sm">
            <span className="text-2xl font-semibold text-accent-300">{items.length}</span>
            <span className="text-zinc-400">
              {items.length === 0 ? (
                filtrando ? (
                  "no hay pendientes con esos filtros"
                ) : (
                  "sin pendientes — no le debemos respuesta a nadie 🎉"
                )
              ) : (
                <>
                  esperando nuestra respuesta · el más viejo lleva{" "}
                  <strong className={masViejo >= 5 ? "text-red-300" : ""}>
                    {masViejo} día{masViejo === 1 ? "" : "s"}
                  </strong>
                  {filtrando && casos.length !== items.length && (
                    <span className="text-zinc-500"> · de {casos.length} en total</span>
                  )}
                </>
              )}
            </span>
          </div>

          {items.length > 0 && (
            <>
              {/* Tarjetas en móvil / tabla en escritorio, como el resto de la app */}
              <div className="card space-y-2 md:hidden">
                {/* Cada tarjeta son DOS enlaces con los IMEIs en medio, fuera de
                    ellos: el «+N más» es un botón y no puede ir dentro de un <a>. */}
                {items.map((c) => (
                  <div
                    key={c.folio}
                    className="rounded-lg border border-ink-700 p-2.5 hover:border-accent-500/40 transition"
                  >
                    <Link to={`/casos/${c.folio}`} onClick={sembrarRecorrido} className="block">
                      <div className="flex items-start justify-between gap-2">
                        <span className="font-mono text-xs text-accent-400 truncate">{c.folio}</span>
                        <span
                          className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] ${tonoDias(
                            c.proveedor_respondio_at ? diasDesde(c.proveedor_respondio_at) : 0,
                          )}`}
                        >
                          {c.proveedor_respondio_at ? diasDesde(c.proveedor_respondio_at) : 0} d
                        </span>
                      </div>
                      <p className="text-sm mt-0.5 truncate">{c.cliente_nombre}</p>
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${ESTADO_CASO_PILL[c.estado]}`}>
                          {c.estado}
                        </span>
                        <TicketProveedorBadge
                          jimi={c.jimi_work_order_no}
                          queclink={c.queclink_ticket_id}
                          estadoJimi={c.jimi_ticket_estado}
                          estadoQueclink={c.queclink_ticket_estado}
                        />
                        <EsperanBadge n={c.menciones_pendientes} sinLeer={c.menciones_sin_leer} />
                        <PosesionBadge posesion={c.posesion} />
                        <CriticoBadge on={c.tiene_imei_critico} />
                        {c.nivel_actividad && (
                          <ActividadPill dias={c.dias_sin_actividad} nivel={c.nivel_actividad} />
                        )}
                      </div>
                    </Link>
                    {c.imeis.length > 0 && (
                      <ImeisLista imeis={c.imeis} max={MAX_IMEIS} className="mt-1" />
                    )}
                    <Link to={`/casos/${c.folio}`} onClick={sembrarRecorrido} className="block">
                      <p className="text-[11px] text-zinc-500 mt-1">
                        {ticketDe(c) ? `${proveedorDe(c)} · ${ticketDe(c)} · ` : ""}
                        {c.asignado_a_nombre ?? "— sin asignar —"}
                      </p>
                      <p className="text-[11px] text-zinc-500">
                        contestó el {c.proveedor_respondio_at ? fechaLocal(c.proveedor_respondio_at) : "—"}
                      </p>
                    </Link>
                  </div>
                ))}
              </div>

              <div className="card hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-500">
                      <th className="pb-2 pr-3">Espera</th>
                      <th className="pb-2 pr-3">Folio</th>
                      <th className="pb-2 pr-3">Cliente</th>
                      <th className="pb-2 pr-3">Equipos</th>
                      <th className="pb-2 pr-3">Estado</th>
                      <th className="pb-2 pr-3">Proveedor</th>
                      <th className="pb-2 pr-3">Asignado</th>
                      <th className="pb-2">Contestó</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c) => (
                      <tr
                        key={c.folio}
                        onClick={(e) => {
                          // Click en cualquier parte de la fila abre el caso, igual
                          // que en /casos: al final son casos. Respeta cmd/ctrl-click
                          // (nueva pestaña vía el folio) y la selección de texto, que
                          // aquí se usa para copiar el número de ticket del proveedor.
                          if (e.metaKey || e.ctrlKey) return;
                          if (window.getSelection()?.toString()) return;
                          abrir(c.folio);
                        }}
                        className="border-t border-ink-700/70 hover:bg-ink-800/50 cursor-pointer"
                      >
                        <td className="py-2 pr-3">
                          <span
                            className={`rounded-full border px-2 py-0.5 text-[11px] whitespace-nowrap ${tonoDias(
                              c.proveedor_respondio_at ? diasDesde(c.proveedor_respondio_at) : 0,
                            )}`}
                          >
                            {c.proveedor_respondio_at ? diasDesde(c.proveedor_respondio_at) : 0} d
                          </span>
                        </td>
                        {/* `whitespace-nowrap`: sin esto el folio se parte en
                            cinco renglones y la fila queda altísima. */}
                        <td className="py-2 pr-3 whitespace-nowrap">
                          <Link
                            to={`/casos/${c.folio}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              sembrarRecorrido();
                            }}
                            className="font-mono text-xs text-accent-400 hover:underline"
                          >
                            {c.folio}
                          </Link>
                        </td>
                        <td className="py-2 pr-3 min-w-[140px]">{c.cliente_nombre}</td>
                        <td className="py-2 pr-3">
                          {c.imeis.length === 0 ? (
                            <span className="text-zinc-600">—</span>
                          ) : (
                            // El «+N más» despliega el resto ahí mismo (antes, tooltip);
                            // corta la propagación, así que no abre el caso.
                            <ImeisLista imeis={c.imeis} max={MAX_IMEIS} className="max-w-[240px]" />
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex flex-wrap items-center gap-1">
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded ${ESTADO_CASO_PILL[c.estado]}`}
                            >
                              {c.estado}
                            </span>
                            <EsperanBadge n={c.menciones_pendientes} sinLeer={c.menciones_sin_leer} />
                            <PosesionBadge posesion={c.posesion} />
                            <CriticoBadge on={c.tiene_imei_critico} />
                            {c.nivel_actividad && (
                              <ActividadPill dias={c.dias_sin_actividad} nivel={c.nivel_actividad} />
                            )}
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-zinc-400 whitespace-nowrap">
                          <TicketProveedorBadge
                            jimi={c.jimi_work_order_no}
                            queclink={c.queclink_ticket_id}
                            estadoJimi={c.jimi_ticket_estado}
                            estadoQueclink={c.queclink_ticket_estado}
                          />
                          {ticketDe(c) && (
                            <span className="ml-1 font-mono text-[11px]">{ticketDe(c)}</span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-zinc-400 text-xs">
                          {c.asignado_a_nombre ?? "— sin asignar —"}
                        </td>
                        <td className="py-2 text-[11px] text-zinc-500 whitespace-nowrap">
                          {c.proveedor_respondio_at ? fechaLocal(c.proveedor_respondio_at) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
